import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import type { Project, ProjectSummary, CreateProjectRequest, UpdateProjectRequest, ProjectFilters, PaginatedResponse } from '@spi/shared';

export function useProjects(filters: ProjectFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status?.length) params.set('status', filters.status.join(','));
  if (filters.crop?.length) params.set('crop', filters.crop.join(','));
  if (filters.country) params.set('country', filters.country);
  if (filters.zone?.length) params.set('zone', filters.zone.join(','));
  if (filters.seedFormat?.length) params.set('seedFormat', filters.seedFormat.join(','));
  if (filters.seedTreatment?.length) params.set('seedTreatment', filters.seedTreatment.join(','));
  if (filters.availableTreatments?.length) params.set('availableTreatments', filters.availableTreatments.join(','));
  if (filters.search) params.set('search', filters.search);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));

  return useQuery({
    queryKey: ['projects', filters],
    queryFn: () => apiFetch<PaginatedResponse<ProjectSummary>>(`/api/projects?${params}`),
  });
}

export function useProject(id: number) {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => apiFetch<Project>(`/api/projects/${id}`),
    enabled: id > 0,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectRequest) =>
      apiFetch<Project>('/api/projects', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateProjectRequest }) =>
      apiFetch<Project>(`/api/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function usePublishProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<Project>(`/api/projects/${id}/publish`, { method: 'PUT' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useCancelProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/projects/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}
